import { Trans } from "@lingui/macro";
import { UiBox, UiButtonBase, UiLink, UiTypography } from "@smp/ui";
import { IconArrowUpRight } from "@tabler/icons-react";
import { forwardRef, useCallback, useMemo } from "react";

import { useContextPolotnoTemplates } from "@/components/features/polotno_templates/context";
import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { UnsplashPayload } from "@/queries/polotno";
import { customGeneratePath } from "@/utils";

import { queryParams } from "../../helpers";
import { useStyles } from "./unsplash_card.css";

type IUnsplashCardProps = {
  payload: UnsplashPayload["results"][number];
};

export const UnsplashCard = forwardRef<any, IUnsplashCardProps>(
  ({ payload }, ref) => {
    const classes = useStyles();
    const {
      state: { searchString, pathPattern, isRoutingDisabled },
      actions: { setActiveEntity },
    } = useContextPolotnoTemplates();

    const firstName = payload.user?.name;
    const userLink = payload.user?.links.html;

    const id = useMemo(
      () =>
        `unsplash:${queryParams.serializeUnsplashData({
          query: searchString,
          url: payload.urls.full,
          user_link: payload.user?.links.html,
          user_name: payload.user?.name,
        })}`,
      [
        payload.urls.full,
        payload.user?.links.html,
        payload.user?.name,
        searchString,
      ],
    );

    const handleClick = useCallback(() => {
      if (!isRoutingDisabled) {
        return;
      }

      setActiveEntity(id);
    }, [id, isRoutingDisabled, setActiveEntity]);

    return (
      <UiButtonBase
        className={classes.wrapper}
        ref={ref}
        component={!isRoutingDisabled ? HelperAdapterLink : "div"}
        to={
          !isRoutingDisabled
            ? customGeneratePath(pathPattern, {
                templateId: id,
              })
            : undefined
        }
        onClick={handleClick}
      >
        <img
          src={payload.urls.small}
          alt=""
          className={classes.image}
          width={payload.width}
          height={payload.height}
        />

        <UiBox className={classes.overlay}>
          <UiBox display="flex" className={classes.text_edit}>
            <UiTypography>
              <Trans>Select this</Trans>
            </UiTypography>
            <IconArrowUpRight />
          </UiBox>

          <UiBox className={classes.caption}>
            <UiTypography className={classes.caption__text}>
              {firstName ? (
                <Trans>
                  Photo by{" "}
                  <UiLink
                    target="_blank"
                    href={userLink}
                    color="inherit"
                    underline="always"
                  >
                    {firstName}
                  </UiLink>{" "}
                  on{" "}
                  <UiLink
                    target="_blank"
                    href="https://unsplash.com"
                    color="inherit"
                    underline="always"
                  >
                    Unsplash
                  </UiLink>
                </Trans>
              ) : (
                <Trans>
                  Photo by{" "}
                  <UiLink
                    target="_blank"
                    href="https://unsplash.com"
                    color="inherit"
                    underline="always"
                  >
                    Unsplash
                  </UiLink>
                </Trans>
              )}
            </UiTypography>
          </UiBox>
        </UiBox>
      </UiButtonBase>
    );
  },
);
